import { motion, useReducedMotion } from 'framer-motion';
import { getBadgeInfo, BADGE_EMOJI } from '../lib/gamification.js';

function formatEuros(cents) {
  const value = Math.max(0, Number(cents) || 0) / 100;
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function timeAgoShort(timestamp) {
  if (!timestamp) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (diffSec < 5) return 'à l’instant';
  if (diffSec < 60) return `il y a ${diffSec}s`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `il y a ${min}min`;
  const h = Math.round(min / 60);
  return `il y a ${h}h`;
}

function VotePodiumStep({ rank, entry, pedestalHeight, gradientClass, color, medal, badge, onSelect }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className="flex flex-col items-center gap-2"
      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { delay: rank === 1 ? 0.05 : rank === 2 ? 0.15 : 0.25 }}
    >
      <button
        type="button"
        onClick={() => entry?.bar_user_id && onSelect?.(entry.bar_user_id)}
        disabled={!entry?.bar_user_id}
        className="flex flex-col items-center"
      >
        <div
          className={`flex h-14 w-14 items-center justify-center rounded-full border-2 text-2xl shadow-lg ${color}`}
        >
          {medal}
        </div>
        <p className="mt-2 max-w-[110px] truncate text-center text-xs font-semibold text-white">
          {badge ? <span aria-hidden>{badge} </span> : null}
          {entry?.username ?? '—'}
        </p>
        <p className="mt-0.5 font-mono text-[11px] font-bold tabular-nums text-white/80">
          {entry ? `${entry.voteCount} ♥` : '—'}
        </p>
      </button>
      <div
        className={`flex w-[88px] items-end justify-center rounded-t-2xl border border-white/15 bg-gradient-to-b ${gradientClass}`}
        style={{ height: pedestalHeight }}
      >
        <span className="pb-1 text-[11px] font-bold uppercase tracking-widest text-white/80">
          #{rank}
        </span>
      </div>
    </motion.div>
  );
}

export default function SocialPodium({
  votePodium = [],
  topContributors = [],
  liveBoostFeed = [],
  participants = 0,
  eventBadges = [],
  featuredBadgeByBarUserId = {},
  onOpenSuggest,
  onOpenUserProfile,
  paidQueueUiEnabled = true,
}) {
  const reduceMotion = useReducedMotion();
  const featEmoji = (barUserId) => {
    const code = barUserId ? featuredBadgeByBarUserId[barUserId] : null;
    return code ? BADGE_EMOJI[code] ?? '🏅' : '';
  };
  const openUser = (barUserId) => {
    if (barUserId && onOpenUserProfile) onOpenUserProfile(barUserId);
  };
  const top3 = votePodium.slice(0, 3);
  const others = votePodium.slice(3, 10);
  const hasVotes = votePodium.length > 0;
  const totalHearts = votePodium.reduce((acc, r) => acc + (Number(r.voteCount) || 0), 0);

  return (
    <motion.section
      className="mt-3 flex flex-col gap-4"
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.3 }}
    >
      <div className="card">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
          Ambiance live
        </p>
        <p className="mt-2 text-sm leading-relaxed text-white/55">
          <span className="font-semibold text-white/85">
            {Number.isFinite(participants) ? participants : 0}
          </span>{' '}
          connecté{participants === 1 ? '' : 's'}
          {' · '}
          <span className="font-mono font-semibold tabular-nums text-white/85">{totalHearts}</span> ♥ au total
          {' · '}
          <span className="font-semibold text-white/75">{votePodium.length}</span> profil
          {votePodium.length !== 1 ? 's' : ''} classé{votePodium.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
            Podium des cœurs
          </p>
          {hasVotes ? (
            <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-medium text-white/55">
              top {Math.min(votePodium.length, 10)}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-white/45">
          Classement des profils selon le total des votes reçus sur toutes leurs suggestions de la soirée.
        </p>

        {hasVotes ? (
          <>
            <div className="mt-5 flex items-end justify-center gap-3">
              <VotePodiumStep
                rank={2}
                entry={top3[1]}
                badge={featEmoji(top3[1]?.bar_user_id)}
                onSelect={openUser}
                pedestalHeight={70}
                gradientClass="from-slate-300/35 to-slate-300/5"
                color="border-slate-200/60 bg-slate-200/15 text-slate-100"
                medal="🥈"
              />
              <VotePodiumStep
                rank={1}
                entry={top3[0]}
                badge={featEmoji(top3[0]?.bar_user_id)}
                onSelect={openUser}
                pedestalHeight={104}
                gradientClass="from-amber-600/35 to-amber-900/10"
                color="border-amber-500/50 bg-amber-500/15 text-amber-100"
                medal="🥇"
              />
              <VotePodiumStep
                rank={3}
                entry={top3[2]}
                badge={featEmoji(top3[2]?.bar_user_id)}
                onSelect={openUser}
                pedestalHeight={50}
                gradientClass="from-orange-500/35 to-orange-500/5"
                color="border-orange-400/60 bg-orange-400/15 text-orange-100"
                medal="🥉"
              />
            </div>

            {others.length > 0 ? (
              <ul className="mt-5 divide-y divide-white/5 rounded-2xl border border-white/10 bg-black/25">
                {others.map((c, idx) => (
                  <li
                    key={c.bar_user_id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5"
                  >
                    <button
                      type="button"
                      onClick={() => openUser(c.bar_user_id)}
                      className="flex min-w-0 items-center gap-3 text-left"
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/5 text-[11px] font-bold text-white/70">
                        {idx + 4}
                      </span>
                      <span className="min-w-0 truncate text-sm font-medium text-white/90">
                        {featEmoji(c.bar_user_id) ? (
                          <span aria-hidden>{featEmoji(c.bar_user_id)} </span>
                        ) : null}
                        {c.username}
                      </span>
                    </button>
                    <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-white/80">
                      {c.voteCount} ♥
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <div className="mt-5 rounded-2xl border border-dashed border-white/15 bg-black/20 p-5 text-center">
            <p className="text-3xl">🏆</p>
            <p className="mt-2 text-sm font-semibold text-white">Pas encore de votes sur le podium</p>
            <p className="mt-1 text-[12px] leading-relaxed text-white/55">
              Suggère une musique : chaque ♥ sur tes pistes te fait grimper ici. Invite les autres à voter
              depuis l’onglet Suggestions.
            </p>
            {onOpenSuggest ? (
              <button
                type="button"
                onClick={onOpenSuggest}
                className="btn-primary mt-3 w-full py-2.5 text-xs"
              >
                + Suggérer une musique
              </button>
            ) : null}
          </div>
        )}
      </div>

      {eventBadges.length > 0 ? (
        <div className="card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
            Badges de la soirée
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-white/45">
            Chaque ligne rappelle le pseudo, le nom du badge en français et ce qu’il signifie pour la soirée.
          </p>
          <ul className="mt-3 space-y-2.5">
            {eventBadges.slice(0, 10).map((b) => {
              const info = getBadgeInfo(b.badge_code);
              return (
                <li
                  key={b.id}
                  className="rounded-xl border border-white/10 bg-black/25 px-3 py-2.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openUser(b.bar_user_id)}
                      className="min-w-0 truncate text-left text-sm font-semibold text-white/90"
                    >
                      {b.username}
                    </button>
                    <span className="shrink-0 rounded-full border border-white/10 bg-white/10 px-2.5 py-0.5 text-center text-[11px] font-semibold leading-tight text-white/85">
                      <span aria-hidden>{info.emoji} </span>
                      {info.title}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-white/50">{info.description}</p>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {paidQueueUiEnabled && topContributors.length > 0 ? (
        <div className="card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
            Contributions (paiements)
          </p>
          <ul className="mt-3 divide-y divide-white/5">
            {topContributors.slice(0, 6).map((c) => (
              <li
                key={c.bar_user_id}
                className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
              >
                <button
                  type="button"
                  onClick={() => openUser(c.bar_user_id)}
                  className="min-w-0 truncate text-left text-sm text-white/90"
                >
                  {featEmoji(c.bar_user_id) ? <span aria-hidden>{featEmoji(c.bar_user_id)} </span> : null}
                  {c.username}
                </button>
                <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-gold">
                  {formatEuros(c.totalCents)} €
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {paidQueueUiEnabled ? (
        <div className="card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
            En direct
          </p>
          {liveBoostFeed.length === 0 ? (
            <p className="mt-3 text-sm text-white/55">
              Le fil live affichera les contributions validées des autres participants en temps réel.
            </p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {liveBoostFeed.slice(0, 8).map((item) => (
                <motion.li
                  key={item.id}
                  initial={reduceMotion ? false : { opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-baseline justify-between gap-2 rounded-xl border border-white/10 bg-black/25 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] leading-snug text-white/85">
                    {item.line}
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wider text-white/40">
                    {timeAgoShort(item.at)}
                  </span>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </motion.section>
  );
}
